
import { useState } from "react";
import { base44 } from "@/api/base44Client";
import { useQuery } from "@tanstack/react-query";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Search, Car, AlertCircle } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function DrivelineStaff() {
  const [searchTerm, setSearchTerm] = useState("");

  const { data: allUsers = [], isLoading } = useQuery({
    queryKey: ['allUsers'],
    queryFn: () => base44.entities.User.list(),
  });

  // Filter only driveline staff
  const drivelineStaff = allUsers.filter(user => user.role === 'user' && user.user_type === 'driveline');

  const filteredStaff = drivelineStaff.filter(staff =>
    staff.full_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    staff.email?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="p-4 md:p-8 bg-gradient-to-br from-slate-50 to-blue-50 min-h-screen">
      <div className="max-w-7xl mx-auto space-y-6">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-orange-100 rounded-xl flex items-center justify-center">
              <Car className="w-6 h-6 text-orange-700" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-slate-900">Driveline Staff</h1>
              <p className="text-slate-600 mt-1">Manage Driveline Staff Accounts and Permissions</p>
            </div>
          </div>
        </div>

        <Alert className="bg-orange-50 border-orange-200">
          <AlertCircle className="w-4 h-4 text-orange-600" />
          <AlertDescription className="text-orange-900">
            <strong>How to add driveline staff:</strong> First invite users through <strong>Dashboard → Users → Invite</strong>, then go to the <strong>Users</strong> page to assign driveline staff permissions.
          </AlertDescription>
        </Alert>

        <Card className="border-none shadow-lg">
          <CardHeader>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-4 h-4" />
              <Input
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Name</TableHead>
                    <TableHead>Email</TableHead>
                    <TableHead>Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {isLoading ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                        Loading...
                      </TableCell>
                    </TableRow>
                  ) : filteredStaff.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} className="text-center py-8 text-slate-500">
                        No driveline staff found
                      </TableCell>
                    </TableRow>
                  ) : (
                    filteredStaff.map((staff) => (
                      <TableRow key={staff.id} className="hover:bg-slate-50">
                        <TableCell className="font-medium">{staff.full_name || '-'}</TableCell>
                        <TableCell>{staff.email}</TableCell>
                        <TableCell>
                          <Badge className="bg-orange-100 text-orange-800">
                            Driveline Staff
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <Card className="border-none shadow-lg bg-blue-50">
          <CardContent className="p-6">
            <h3 className="font-semibold text-slate-900 mb-2">Driveline Staff Permissions:</h3>
            <ul className="list-disc list-inside text-sm text-slate-700 space-y-1">
              <li>Quick EA check by driveline tag number</li>
              <li>See if student is Present in EA (real-time)</li>
              <li>Perfect for car rider line during dismissal</li>
              <li>Cannot access rosters, reports, or student database</li>
              <li>Cannot manage users or system settings</li>
              <li>Simple, focused interface for dismissal time</li>
            </ul>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
